import React, { Component, Fragment } from 'react';
import { 
    withStyles, Divider, useMediaQuery,
} from '@material-ui/core';
import { useStyles } from '../../components/common/useStyles';
import '../../components/common/Common.css'
import { withRouter } from 'react-router-dom';
import constant from '../../components/common/Constants';
import routeService from '../../services/Routing';

const withMediaQuery = (...args) => Component => props => {
    const mediaQuery = useMediaQuery(...args);    
    return <Component mediaQuery={mediaQuery} {...props} />;
};

class Privacy extends Component {
    constructor(props) {
        super(props);
        this.state = {
        }
    }

    componentDidMount() {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    componentDidUpdate = (prevProps, prevState) => {
        if(this.props.searchValue !== prevProps.searchValue) {
            routeService.routePage(this.props, constant.home);
        }
    }

    render() {
        const { classes, mediaQuery } = this.props;

        return (
            <Fragment>
                <h1 style={{marginTop: mediaQuery ? '65px' : '110px'}} 
                    className={classes.aboutTitle}>
                        PRIVACY NOTICE
                </h1>

                <Divider orientation="horizontal" style={{marginTop: 20}} />
                
                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <b>Last updated March 16, 2022</b>
                </div>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p>
                        This privacy notice for EdWhat <b>("Company," "we," "us," or "our")</b>, describes how and why 
                        we might collect, store, use, and/or share <b>("process")</b> your information when you use our 
                        services <b>("Services")</b>, such as when you:
                    </p>
                    <p>
                        <ul>
                            <li>
                                Visit our website at <a href="https://edwhat.com/">https://edwhat.com</a>, or any website of ours that links to this privacy notice
                            </li>
                            <li>
                                Engage with us in other related ways, including any sales, marketing, or events
                            </li>
                        </ul>
                    </p>
                    <p>
                        <b>Questions or concerns?</b> Reading this privacy notice will help you understand your privacy 
                        rights and choices. If you do not agree with our policies and practices, please do not use 
                        our Services. If you still have any questions or concerns, please contact us at <a href="mailto:team@edwhat.com">team@edwhat.com</a>.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    SUMMARY OF KEY POINTS
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>
                            This summary provides key points from our privacy notice, but you can find out more details 
                            about any of these topics below
                        </b>
                    </p>
                    <p>
                        <b>What personal information do we process?</b> When you visit, use, or navigate our 
                        Services, we may process personal information depending on how you interact with EdWhat 
                        and the Services, the choices you make, and the products and features you use.
                    </p>
                    <p>
                        <b>Do we process any sensitive personal information?</b> We do not process sensitive personal information.
                    </p>
                    <p>
                        <b>Do you receive any information from third parties?</b> We do not receive any information from third parties.
                    </p>
                    <p>
                        <b>How do you process my information?</b> We process your information to provide, improve, 
                        and administer our Services, communicate with you, for security and fraud prevention, 
                        and to comply with law. We may also process your information for other purposes with your 
                        consent. We process your information only when we have a valid legal reason to do so.
                    </p>
                    <p>
                        <b>In what situations and with which parties do we share personal information?</b> We may 
                        share information in specific situations and with specific third parties. We also share 
                        data with third party framework hyvor-talk. Click here to check their privacy policies.
                    </p>
                    <p>
                        <b>How do we keep your information safe?</b> We have organizational and technical processes 
                        and procedures in place to protect your personal information. However, no electronic 
                        transmission over the internet or information storage technology can be guaranteed to be 
                        100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other 
                        unauthorized third parties will not be able to defeat our security and improperly collect, 
                        access, steal, or modify your information.
                    </p>
                    <p>
                        <b>What are your rights?</b> Depending on where you are located geographically, the 
                        applicable privacy law may mean you have certain rights regarding your personal information.
                    </p>
                    <p>
                        <b>How do I exercise my rights?</b> The easiest way to exercise your rights is by filling 
                        out our data subject request form available 
                        here: <a href="https://edwhat.com/#/myprofile">https://edwhat.com/#/myprofile</a>, or by 
                        contacting us. We will consider and act upon any request in accordance with applicable 
                        data protection laws.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    1. WHAT INFORMATION DO WE COLLECT?
                </h3>

                <div className={classes.aboutTopContent}>
                    Personal information you disclose to us
                </div>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We collect personal information that you provide to us.
                    </p>
                    <p>
                        We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
                    </p>
                    <p>
                        <b>Personal Information Provided by You.</b> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
                    </p>
                    <p>
                        <ul>
                            <li>
                                names
                            </li>
                            <li>
                                email addresses
                            </li>
                        </ul>
                    </p>
                    <p>
                        <b>Sensitive Information.</b> We do not process sensitive information.
                    </p>
                    <p>
                        <b>Social Media Login Data.</b> We may provide you with the option to register with us using your existing social media account details, like your Facebook, Twitter, or other social media account. 
                    </p>
                    <p>
                        All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
                    </p>

                    <div className={classes.aboutTopContent}>
                        Information automatically collected
                    </div>

                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.
                    </p>
                    <p>
                        We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information. This information is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.
                    </p>
                    <p>
                        Like many businesses, we also collect information through cookies and similar technologies. You can find out more about this in our Cookie Notice: <a href="https://hyvor.com/privacy-policy">https://hyvor.com/privacy-policy</a>.
                    </p>
                    <p>
                        The information we collect includes:
                    </p>
                    <p>
                        <ul>
                            <li>
                                Log and Usage Data. Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called "crash dumps"), and hardware settings).
                            </li>
                        </ul>
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    2. HOW DO WE PROCESS YOUR INFORMATION?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.
                    </p>
                    <p>
                        <b>
                            We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
                        </b>
                    </p>
                    <p>
                        <ul>
                            <li>
                                <b>To facilitate account creation and authentication and otherwise manage user accounts.</b> We may process your information so you can create and log in to your account, as well as keep your account in working order.
                            </li>
                            <li>
                                <b>To deliver and facilitate delivery of services to the user.</b> We may process your information to provide you with the requested service.
                            </li>
                            <li>
                                <b>To send administrative information to you.</b> We may process your information to send you details about our products and services, changes to our terms and policies, and other similar information.
                            </li>
                            <li>
                                <b>To fulfill and manage your orders.</b> We may process your information to fulfill and manage your orders, payments, returns, and exchanges made through the Services.
                            </li>
                            <li>
                                <b>To enable user-to-user communications.</b> We may process your information if you choose to use any of our offerings that allow for communication with another user.
                            </li>
                            <li>
                                <b>To evaluate and improve our Services, products, marketing, and your experience.</b> We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience.
                            </li>
                        </ul>
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We may share information in specific situations described in this section and/or with the following third parties.
                    </p>
                    <p>
                        <b>Vendors, Consultants, and Other Third-Party Service Providers.</b> We may share your 
                        data with third-party vendors, service providers, contractors, or agents <b>(“third parties”)</b> 
                        who perform services for us or on our behalf and require access to such information to do 
                        that work. The third parties we may share personal information with are as follows:
                    </p>
                    <p>
                        <ul>
                            <li>
                                <b>User Commenting & Forums</b>
                            </li>
                        </ul>
                    </p>
                    <p>
                        Hyvor Talk
                    </p>
                    <p>
                        We also may need to share your personal information in the following situations:
                    </p>
                    <p>
                        <ul>
                            <li>
                                <b>Business Transfers.</b> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
                            </li>
                            <li>
                                <b>Business Partners.</b> We may share your information with our business partners to offer you certain products, services, or promotions.
                            </li>
                        </ul>
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We may use cookies and other tracking technologies to collect and store your information.
                    </p>
                    <p>
                        We may use cookies and similar tracking technologies (like web beacons and pixels) to 
                        access or store information. Specific information about how we use such technologies and 
                        how you can refuse certain cookies is set out in our Cookie 
                        Notice: <a href="https://hyvor.com/privacy-policy">https://hyvor.com/privacy-policy</a>.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> If you choose to register or log in to our services using a social media account, we may have access to certain information about you.
                    </p>
                    <p>
                        Our Services offer you the ability to register and log in using your third-party social media account details (like your Facebook or Twitter logins). Where you choose to do this, we will receive certain profile information about you from your social media provider. The profile information we receive may vary depending on the social media provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a social media platform.
                    </p>
                    <p>
                        We will use the information we receive only for the purposes that are described in this privacy notice or that are otherwise made clear to you on the relevant Services. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider. We recommend that you review their privacy notice to understand how they collect, use and share your personal information, and how you can set your privacy preferences on their sites and apps.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    6. HOW LONG DO WE KEEP YOUR INFORMATION?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.
                    </p>
                    <p>
                        We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
                    </p>
                    <p>
                        When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    7. HOW DO WE KEEP YOUR INFORMATION SAFE?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We aim to protect your personal information through a system of organizational and technical security measures.
                    </p>
                    <p>
                        We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    8. DO WE COLLECT INFORMATION FROM MINORS?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> We do not knowingly collect data from or market to children under 18 years of age.
                    </p>
                    <p>
                        We do not knowingly solicit data from or market to children under 18 years of age. By 
                        using the Services, you represent that you are at least 18 or that you are the parent or 
                        guardian of such a minor and consent to such minor dependents use of the Services. If we 
                        learn that personal information from users less than 18 years of age has been collected, 
                        we will deactivate the account and take reasonable measures to promptly delete such data 
                        from our records. If you become aware of any data we may have collected from children 
                        under age 18, please contact us at <a href="mailto:team@edwhat.com">team@edwhat.com</a>.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    9. WHAT ARE YOUR PRIVACY RIGHTS?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> You may review, change, or terminate your account at any time.
                    </p>
                    <p>
                        If you are located in the EEA or UK and you believe we are unlawfully processing your 
                        personal information, you also have the right to complain to your local data protection 
                        supervisory authority. You can find their contact details 
                        here: <a href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm">
                        https://ec.europa.eu/</a>.
                    </p>
                    <p>
                        If you are located in Switzerland, the contact details for the data protection authorities 
                        are available here: <a href="https://www.edoeb.admin.ch/edoeb/en/home.html">
                        https://www.edoeb.admin.ch/</a>.
                    </p>
                    <p>
                        <b style={{textDecoration: 'underline'}}>Withdrawing your consent:</b> If we are relying on your consent to process your 
                        personal information, which may be express and/or implied consent depending on the 
                        applicable law, you have the right to withdraw your consent at any time. You can withdraw 
                        your consent at any time by contacting us by using the contact details provided in the 
                        section below.
                    </p>
                    <p>
                        However, please note that this will not affect the lawfulness of the processing before its withdrawal, nor when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.
                    </p>
                    <div className={classes.aboutTopContent}>
                        Account Information
                    </div>
                    <p>
                        If you would at any time like to review or change the information in your account or 
                        terminate your account, you can: Upon your request to terminate your account, we will 
                        deactivate or delete your account and information from our active databases. However, 
                        we may retain some information in our files to prevent fraud, troubleshoot problems, 
                        assist with any investigations, enforce our legal terms and/or comply with applicable 
                        legal requirements.
                    </p>
                    <p>
                        <b style={{textDecoration: 'underline'}}>Cookies and similar technologies:</b> Most Web 
                        browsers are set to accept cookies by default. If you prefer, you can usually choose to 
                        set your browser to remove cookies and to reject cookies. If you choose to remove cookies 
                        or reject cookies, this could affect certain features or services of our Services. To opt 
                        out of interest-based advertising by advertisers on our Services 
                        visit <a href="http://www.aboutads.info/choices/">http://www.aboutads.info/choices/</a>. For 
                        further information, please see our Cookie 
                        Notice: <a href="https://hyvor.com/privacy-policy">https://hyvor.com/privacy-policy</a>.
                    </p>
                    <p>
                        If you have questions or comments about your privacy rights, you may email us 
                        at <a href="mailto:team@edwhat.com">team@edwhat.com</a>.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    10. CONTROLS FOR DO-NOT-TRACK FEATURES
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p>
                        Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this privacy notice.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    11. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> Yes, if you are a resident of California, you are granted specific rights regarding access to your personal information.
                    </p>
                    <p>
                        California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us using the contact information provided below.
                    </p>
                    <p>
                        If you are under 18 years of age, reside in California, and have a registered account with Services, you have the right to request removal of unwanted data that you publicly post on the Services. To request removal of such data, please contact us using the contact information provided below and include the email address associated with your account and a statement that you reside in California. We will make sure the data is not publicly displayed on the Services, but please be aware that the data may not be completely or comprehensively removed from all our systems (e.g., backups, etc.).
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    12. DO WE MAKE UPDATES TO THIS NOTICE?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p style={{fontStyle:'italic'}}>
                        <b>In Short:</b> Yes, we will update this notice as necessary to stay compliant with relevant laws.
                    </p>
                    <p>
                        We may update this privacy notice from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy notice frequently to be informed of how we are protecting your information.
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p>
                        If you have questions or comments about this notice, you may email us 
                        at <a href="mailto:team@edwhat.com">team@edwhat.com</a> or by post to:
                    </p>
                    <p>
                        EdWhat
                        <br/>
                        __________
                        <br/>
                        Bengaluru, Karnatka 560017
                        <br/>
                        India
                    </p>
                </div>

                <h3 className={classes.aboutSubTitle}>
                    14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                </h3>

                <div className={classes.aboutContent} style={{marginTop: 20}}>
                    <p>
                        Based on the applicable laws of your country, you may have the right to request access to 
                        the personal information we collect from you, change that information, or delete it in 
                        some circumstances. To request to review, update, or delete your personal information, 
                        please visit: <a href="https://edwhat.com/#/myprofile">https://edwhat.com/#/myprofile</a>.
                    </p>
                </div>
            </Fragment>
        );
    }
}

export default withRouter(withStyles(useStyles)(withMediaQuery('(min-width:600px)')(Privacy)))